import { Component, Input, OnInit } from '@angular/core';
import { Filter, FilterItem, FiltersEditedItems, FilterTag, Page } from '../../shared/models/Contact';
import { ContactsService } from '../../shared/services/contacts.service';
import { MsisdnService } from '../../../shared/msisdn/msisdn.service';
import { Store } from '@ngrx/store';
import { IState } from '../../../shared/models/state';
import {
  getEditedFilterItemsSelector,
  getEditedFiltersSelector,
  getOpenedFilterItemSelector,
  getTempPageSelector
} from '../../contacts.reducer';
import {
  AddTagAction,
  GetFilterItemsSuccessAction,
  RemoveTagAction,
  RevertEditedFilterChanges,
  UpdateTempPageWithItemsAction
} from '../../contacts.actions';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import * as countryData from 'country-data';
import { PricingNumbersService } from '../../../shared/services/pricing-numbers.service';
import { CallingCountriesResponse, CountryCallingInfo } from './CountryCallingInfo';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { Msisdn } from '../../../shared/msisdn/msisdns';
import { FilterService } from '../../shared/services/filter.service';
import { AsYouType } from 'libphonenumber-js';

@Component({
  selector: 'cc-filter-phone-number',
  animations: [
    trigger('openClose', [
      state(
        'open',
        style({
          opacity: 1,
          top: 0,
          right: 0
        })
      ),
      state(
        'closed',
        style({
          opacity: 1,
          top: 0,
          right: '-100%'
        })
      ),
      transition('open => closed', [animate('1s ease')]),
      transition('closed => open', [animate('0.5s')])
    ])
  ],
  templateUrl: './filter-phone-number.component.html',
  styleUrls: ['./filter-phone-number.component.scss']
})
export class FilterPhoneNumberComponent implements OnInit {
  @Input() private initialCountryCode: string;

  public filtersOpened: boolean;
  public phoneNumberFilterOpened: boolean;
  public phoneNumbersList: FilterItem[];
  public filterOpenedName: string;
  public editedFiltersList: string[];
  public currentTempFilters: Filter;
  public isFilteringReady: boolean;
  public clientPhoneNumbersForm: FormGroup;
  public countryFilter: FormControl = new FormControl();
  public filteredNumbers: Observable<CountryCallingInfo[]>;
  public currentNumber: CountryCallingInfo;
  public onDestroy = new Subject<void>();
  public countries: CountryCallingInfo[];
  public detectAutomaticallyItem: CountryCallingInfo = {
    countryCode: '',
    phonePrefix: '',
    flagEmoji: '',
    countryName: 'Detect automatically'
  };
  public availableCountries: CountryCallingInfo[];
  public formatter = new AsYouType();

  constructor(
    public contactsService: ContactsService,
    private store: Store<IState>,
    private fb: FormBuilder,
    public pricingNumbersService: PricingNumbersService,
    public msisdnService: MsisdnService,
    public filterService: FilterService
  ) {
    this.countries = [this.detectAutomaticallyItem, ...this.mapCountryData(countryData.callingCountries.all)];
  }

  ngOnInit() {
    this.contactsService.toggleOpeningFilters.subscribe((opener: boolean) => {
      this.filtersOpened = opener;
    });

    this.contactsService.toggleOpeningPhoneNumbersFilters.subscribe(opener => {
      this.phoneNumberFilterOpened = opener;
      this.isFilteringReady = false;
      if (opener) {
        this.filterService.getFilterSnapshot();
      }
    });

    this.store.select(getEditedFilterItemsSelector).subscribe((response: FiltersEditedItems) => {
      if (response && response.clientPhoneNumbers) {
        this.phoneNumbersList = response.clientPhoneNumbers;
      }
    });

    this.store.select(getOpenedFilterItemSelector).subscribe((filter: string) => {
      this.filterOpenedName = filter;
    });

    this.store.select(getEditedFiltersSelector).subscribe((editedFiltersList: string[]) => {
      this.editedFiltersList = editedFiltersList;
    });

    this.store.select(getTempPageSelector).subscribe((page: Page) => {
      if (page) {
        this.currentTempFilters = page.filter;
      }
    });

    this.contactsService.onPhoneNumberFilterOff.subscribe((tag: FilterTag) => {
      if (tag && (!!this.phoneNumbersList || !!this.phoneNumbersList.length)) {
        const itemToRemove = this.phoneNumbersList.find(item => item.name === tag.name);
        if (itemToRemove) {
          this.removeFilterRange(itemToRemove);
        }
      }
    });

    this.msisdnService.msisdns().subscribe((numberList: Msisdn[]) => {
      const initialNumber = numberList.filter(el => el.initial === true)[0];
      const alternativeNumber = numberList.filter(el => el.initial === false)[0];
      const number = initialNumber ? initialNumber : alternativeNumber;

      this.currentNumber = {
        countryCode: number.isoCountry,
        countryName: this.getPrefix(number.isoCountry).countryName,
        phonePrefix: this.getPrefix(number.isoCountry).phonePrefix
      };
      this.initialCountryCode = this.currentNumber.countryCode;
    });

    this.filteredNumbers = this.countryFilter.valueChanges.pipe(startWith(''), map(value => this.filter(value)));
    this.currentNumber = this.findInitialCountry();
    this.initNumberForm();
    this.countryFilter.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.filter(this.countryFilter.value);
    });

    this.isFilteringReady = false;
  }

  initNumberForm() {
    this.clientPhoneNumbersForm = this.fb.group({
      prefixFormControl: this.fb.group({
        countryCode: [''],
        phonePrefix: [''],
        flagEmoji: [''],
        countryName: ['Detect automatically']
      }),
      clientPhoneNumber: ['']
    });
  }

  private mapCountryData(countriesResponse: CallingCountriesResponse[]): CountryCallingInfo[] {
    return countriesResponse.map(country => ({
      countryCode: country.alpha2,
      phonePrefix: country.countryCallingCodes[0],
      flagEmoji: country.emoji,
      countryName: country.name
    }));
  }

  getPrefix(countryCode) {
    return {
      phonePrefix: countryData.callingCountries.all.filter(data => data.alpha2 === countryCode.toUpperCase())[0]
        .countryCallingCodes[0],
      countryName: countryData.callingCountries.all.filter(data => data.alpha2 === countryCode.toUpperCase())[0].name
    };
  }

  private findInitialCountry(): CountryCallingInfo {
    return this.initialCountryCode
      ? this.countries.find(option => option.countryCode.toUpperCase() === this.initialCountryCode)
      : this.detectAutomaticallyItem;
  }

  filter(value: string | CountryCallingInfo) {
    if (this.isCountryCallingInfo(value)) {
      return;
    }
    const filterValue = value.toLowerCase();
    return this.countries.filter(option => option.countryName.toLowerCase().includes(filterValue));
  }

  isCountryCallingInfo(val: any): val is CountryCallingInfo {
    return typeof val === 'object' && 'countryName' in val;
  }

  getCountries(countries) {
    return countries.map(el => {
      const country = this.countries.find(e => e.countryCode.toLowerCase() === el.isoCountry.toLowerCase());
      return {
        countryName: country.countryName,
        countryCode: country.countryCode,
        phonePrefix: country.phonePrefix
      };
    });
  }

  removeFilterRange(PhoneNumberItem: FilterItem) {
    const oldPhoneNumbersList = this.phoneNumbersList;
    const listOfPhoneNumbers = this.phoneNumbersList.filter(numberItem => numberItem.name !== PhoneNumberItem.name);
    const listOfPhoneNumbersToRequest = listOfPhoneNumbers.map(number => number.name);

    this.store.dispatch(
      new RemoveTagAction({
        name: PhoneNumberItem.name,
        source: this.filterOpenedName,
        title: this.contactsService.getFilterTitle(this.filterOpenedName)
      })
    );

    this.phoneNumbersList = listOfPhoneNumbers;

    this.filterService.updateStoreEditedFiltersList(
      this.phoneNumbersList,
      listOfPhoneNumbersToRequest,
      this.filterOpenedName
    );
    this.filterService.updateStoreRequestFilters(listOfPhoneNumbersToRequest, this.filterOpenedName);

    this.store.dispatch(
      new GetFilterItemsSuccessAction({ list: this.phoneNumbersList, filterName: this.filterOpenedName })
    );
    this.isFilteringReady = oldPhoneNumbersList !== this.phoneNumbersList;
  }

  backToFilters() {
    this.store.dispatch(new RevertEditedFilterChanges());
    this.contactsService.showSearchFilters();
    this.contactsService.hidePhoneNumbersFilters();
  }

  phoneNumbersFiltersDone() {
    this.contactsService.showSearchFilters();
    this.contactsService.hidePhoneNumbersFilters();
    this.contactsService.onDoneFilter();
    this.store.dispatch(new UpdateTempPageWithItemsAction(this.filterOpenedName));
  }

  translateFilterName(filterName: string): string {
    return this.contactsService.getFilterTitle(filterName);
  }

  createFilter() {
    if (!this.isNotValid()) {
      let newPhoneNumbersList;
      const oldPhoneNumbersList = this.phoneNumbersList;
      const newNumber = this.clientPhoneNumbersForm.get('clientPhoneNumber').value;
      const newNumberCountryCode = this.currentNumber ? this.currentNumber.countryCode : '';

      if (newNumber && newNumber.length > 6) {
        const newFilterItem = {
          name: newNumber,
          isFilter: true,
          type: this.filterOpenedName,
          countryCode: newNumberCountryCode
        };

        if (this.phoneNumbersList) {
          newPhoneNumbersList = !this.phoneNumbersList.find(numberItem => numberItem.name === newFilterItem.name)
            ? [...this.phoneNumbersList, newFilterItem]
            : this.phoneNumbersList;
        } else {
          newPhoneNumbersList = [newFilterItem];
        }

        this.phoneNumbersList = newPhoneNumbersList;
        this.store.dispatch(
          new GetFilterItemsSuccessAction({ list: this.phoneNumbersList, filterName: 'clientPhoneNumbers' })
        );
        this.filterService.updateStoreEditedFiltersList(
          this.phoneNumbersList,
          this.editedFiltersList,
          this.filterOpenedName
        );

        this.phoneNumbersList.forEach(clientNumber => {
          this.store.dispatch(
            new AddTagAction({
              name: clientNumber.name,
              source: 'clientPhoneNumbers',
              title: this.contactsService.getFilterTitle('clientPhoneNumbers')
            })
          );
        });
      }

      this.clearInput();
      this.isFilteringReady =
        !!this.phoneNumbersList && !!this.phoneNumbersList.length && oldPhoneNumbersList !== this.phoneNumbersList;
    }
  }

  clearInput() {
    this.clientPhoneNumbersForm.get('clientPhoneNumber').setValue(' ');
    this.clientPhoneNumbersForm.get('clientPhoneNumber').markAsUntouched();
  }

  isNotValid() {
    const numberInput = this.clientPhoneNumbersForm.get('clientPhoneNumber');
    return !numberInput.dirty || (numberInput.dirty && numberInput.hasError('phone'));
  }

  filtersResign() {
    this.contactsService.closeAllFilters();
    this.store.dispatch(
      new GetFilterItemsSuccessAction({ list: this.phoneNumbersList, filterName: this.filterOpenedName })
    );
  }

  getCountryCode(number: string) {
    const asYouType = new AsYouType();
    asYouType.input(number);
    return asYouType.country.toLowerCase();
  }

  exitFilter($event) {
    if ($event.target.className === 'nav-text' && this.filtersOpened && this.phoneNumberFilterOpened) {
      this.filtersResign();
    }
  }
}
